/** @jsx jsx */
import { jsx } from "@emotion/core";
import * as React from "react";
import { LanguageProficiencyProps } from "./interfaces";
import { languageProficiencyStyle } from "./styles";
import { OptionField } from "@curefit/pandora";
import { RadioButtonSelect } from "../../atoms/RadioButtonSelect";
import { URL } from "../../API/urlEndpoints";
import {
  booleanRadioValues,
  ButtonClickType,
  inputFieldMandatoryLabel,
  inputFieldOptionalLabel,
} from "../../constants/commons";
import * as _ from "lodash";
import { ChangeEvent } from "react";
import { ISelectOption } from "@curefit/pandora/dist/components/optionField/optionField.interfaces";
import { responsiveStyles } from "../../commons/styles";
import useSTR from "react-str";
import { getData, postData } from "../../utils/fetchers";
import { toast } from "../../utils/toast";
import {
  LanguageProficiencyInfo,
  BooleanTypes,
  RegisterationFields,
  FieldRenderState,
} from "../../commons/interfaces";
import {
  getArrayOFStringFromObjArray,
  displayConfirmationWhenWindowCloses,
  useDataFromServerToDropdwon,
  showDisplayHeadingAndContentFromLoadingStatus,
  convertStringArraytoObjectArray,
  getFieldConfigStatus,
  isFieldOptional,
  getErrorForField,
} from "../../utils/helpers";
import { RegistrationFooterButton } from "../../components/RegistrationFooterButton";
import { RegistrationLayout } from "../../components/RegistrationLayout";
import { ShowMessage } from "../../components/ShowMessage";
import { useUserContext } from "../../hooks/cookieHooks";
import { ErrorComponent } from "../../molecules/ErrorComponent";
export const LanguageProficiency: React.FunctionComponent<LanguageProficiencyProps> = (
  props
) => {
  const {
    pageNavigationDetails,
    onNextClick,
    onBackButtonClick,
    onSaveAndExitClick,
    SuccessFullSaveObj,
  } = props;
  const { nextScreen } = pageNavigationDetails;
  // Calling the SSO Hook to retrieve user information of the logged in user
  const user = useUserContext();

  // State checking whether user can be navigated to the next page or not
  const [isNextPageNavAllowed, setIsNextPageNavAllowed] = React.useState<
    boolean
  >(false);

  // State to maintain other languages selected
  const [secondaryLanguage, setSecondaryLanguage] = React.useState<
    ISelectOption
  >();

  // State to check whether language Profficiency in ENGLISH is achieved or not
  const [fluencyInEnglish, setFluencyInEnglish] = React.useState<
    BooleanTypes
  >();

  // [GET] On page load, for fetching the details of Basic Information
  const [
    ,
    {
      result: getLanguageProficiencyData,
      revalidator: refetchLanguageProficiencyData,
      loading: getLangProficiencyLoading,
      error: getLangProficiencyError,
    },
  ] = useSTR(getData);

  // [GET] To fetch the languages from API
  const [
    ,
    {
      result: languagesList,
      revalidator: refetchLanguagesList,
      loading: loadingLanguagesList,
      error: errorLanguagesList,
    },
  ] = useSTR(getData);

  // [POST] For Saving data on page.
  const [
    ,
    {
      result: postLangProficiency,
      revalidator: refetchPostLangProficiency,
      loading: postLangProficiencyLoading,
      error: postLangProficiencyError,
    },
  ] = useSTR(postData);

  // Callback from the parent Component to SAVE AND QUIT
  React.useEffect(() => {
    onSaveAndExitClick && onPositiveActionClick(ButtonClickType.SAVE_AND_EXIT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSaveAndExitClick]);

  // On page load, calling the GET API
  React.useEffect(() => {
    refetchLanguageProficiencyData(
      `${URL.getLanguageProficiency(_.get(user, "empId"))}`
    );

    // To fetch the languages
    refetchLanguagesList(`${URL.getLanguageList}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function which is called whewn the browser closes
  displayConfirmationWhenWindowCloses();

  /// Fetching data From Server
  React.useEffect(() => {
    if (getLanguageProficiencyData) {
      const {
        isComfortableInEnglish,
        languagesKnown,
      } = getLanguageProficiencyData;
      // Secondary Languages
      languagesKnown &&
        setSecondaryLanguage(convertStringArraytoObjectArray(languagesKnown));
      // comfortable Speaking English
      isComfortableInEnglish &&
        setFluencyInEnglish(isComfortableInEnglish?.toUpperCase());
    }
  }, [getLanguageProficiencyData]);

  // Showing the success message in a toast component
  React.useEffect(() => {
    if (postLangProficiency) {
      postLangProficiency?.message &&
        toast.success(postLangProficiency.message);
      if (isNextPageNavAllowed) {
        onNextClick(nextScreen);
      } else {
        SuccessFullSaveObj(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postLangProficiency, isNextPageNavAllowed]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Function which is called when secondary language is selected from dropdwon
  const onSecondaryLanguageSelect = (options: ISelectOption) => {
    setSecondaryLanguage(options);
  };

  const onLanguageComfortableClick = (event: ChangeEvent<HTMLInputElement>) => {
    setFluencyInEnglish(event.target.value as BooleanTypes);
  };

  const onPositiveActionClick = (buttonClickType: ButtonClickType) => {
    const languageProficiencyObj = constructDataForPOSTAPI();
    switch (buttonClickType) {
      case ButtonClickType.NEXT:
        let paramsObj = {
          ...languageProficiencyObj,
          customHeader: { change_state: true },
        };
        refetchPostLangProficiency(
          `${URL.postLanguageProficiency(_.get(user, "empId"))}`,
          paramsObj
        );
        setIsNextPageNavAllowed(true);
        break;

      case ButtonClickType.SAVE_AND_EXIT:
        refetchPostLangProficiency(
          `${URL.postLanguageProficiency(_.get(user, "empId"))}`,
          languageProficiencyObj
        );
        setIsNextPageNavAllowed(false);
        break;
      default:
        break;
    }
  };

  const constructDataForPOSTAPI = React.useCallback(() => {
    let languageProficiencyObj: LanguageProficiencyInfo = {
      isComfortableInEnglish: fluencyInEnglish as BooleanTypes,
      languagesKnown:
        (secondaryLanguage &&
          Object.keys(secondaryLanguage) &&
          getArrayOFStringFromObjArray(secondaryLanguage)) ||
        [],
    };
    return languageProficiencyObj;
  }, [fluencyInEnglish, secondaryLanguage]);

  // Variable which shows the loading state
  const isScreenLoading = React.useMemo(() => {
    return (
      getLangProficiencyLoading ||
      loadingLanguagesList ||
      postLangProficiencyLoading
    );
  }, [
    getLangProficiencyLoading,
    loadingLanguagesList,
    postLangProficiencyLoading,
  ]);

  // Hook which is called whenever there is an error in either GET/POST calls
  const showErrorComponent = React.useMemo(() => {
    return (
      getLangProficiencyError || errorLanguagesList || postLangProficiencyError
    );
  }, [postLangProficiencyError, errorLanguagesList, getLangProficiencyError]);

  // For showing/hiding the English Comfortability Radio Button
  const showEnglishgComfortablility = React.useMemo(() => {
    return (
      getLanguageProficiencyData &&
      getLanguageProficiencyData.fieldsconfig &&
      _.keys(getLanguageProficiencyData.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getLanguageProficiencyData.fieldsconfig,
        RegisterationFields.ENGLISH_COMFORTABLITY
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getLanguageProficiencyData]);

  // For showing the label OPTIONAL for the field [English Comfortability]
  const showEnglishgComfortablilityOptionalLabel = React.useMemo(() => {
    return (
      getLanguageProficiencyData &&
      getLanguageProficiencyData.fieldsconfig &&
      _.keys(getLanguageProficiencyData.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getLanguageProficiencyData.fieldsconfig,
          RegisterationFields.ENGLISH_COMFORTABLITY
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getLanguageProficiencyData]);

  // For showing/hiding the Language Comfortability Radio Button
  const showLanguageComfortablility = React.useMemo(() => {
    return (
      getLanguageProficiencyData &&
      getLanguageProficiencyData.fieldsconfig &&
      _.keys(getLanguageProficiencyData.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getLanguageProficiencyData.fieldsconfig,
        RegisterationFields.LANGUAGE_COMFORTABILITY
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getLanguageProficiencyData]);

  // For showing the label OPTIONAL for the field [English Comfortability]
  const showLanguageComfortablilityOptionalLabel = React.useMemo(() => {
    return (
      getLanguageProficiencyData &&
      getLanguageProficiencyData.fieldsconfig &&
      _.keys(getLanguageProficiencyData.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getLanguageProficiencyData.fieldsconfig,
          RegisterationFields.LANGUAGE_COMFORTABILITY
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getLanguageProficiencyData]);

  // To show error [English Comfortability]
  const englishComfortabilityError = React.useMemo(() => {
    return (
      getLanguageProficiencyData &&
      getLanguageProficiencyData.errors &&
      getErrorForField(
        RegisterationFields.ENGLISH_COMFORTABLITY,
        getLanguageProficiencyData.errors
      )
    );
  }, [getLanguageProficiencyData]);

  // To show error [Language Comfortability]
  const otherLangComfortabilityError = React.useMemo(() => {
    return (
      getLanguageProficiencyData &&
      getLanguageProficiencyData.errors &&
      getErrorForField(
        RegisterationFields.LANGUAGE_COMFORTABILITY,
        getLanguageProficiencyData.errors
      )
    );
  }, [getLanguageProficiencyData]);

  // For getting the language Dropdwon values
  const languageDropdownValue = useDataFromServerToDropdwon(languagesList);

  const isSubmitAllowed = React.useMemo(() => {
    return !(
      (showLanguageComfortablility
        ? secondaryLanguage && _.keys(secondaryLanguage).length > 0
        : true) && (showEnglishgComfortablility ? fluencyInEnglish : true)
    );
  }, [
    secondaryLanguage,
    fluencyInEnglish,
    showEnglishgComfortablility,
    showLanguageComfortablility,
  ]);

  return (
    <span css={[responsiveStyles, languageProficiencyStyle]}>
      <ShowMessage
        visible={isScreenLoading}
        loading={isScreenLoading}
        heading={
          showDisplayHeadingAndContentFromLoadingStatus({
            preLoginScreen: getLangProficiencyLoading || loadingLanguagesList,
            postLoginScreen: postLangProficiencyLoading,
          })?.loadingHeading
        }
        description={
          showDisplayHeadingAndContentFromLoadingStatus({
            preLoginScreen: getLangProficiencyLoading || loadingLanguagesList,
            postLoginScreen: postLangProficiencyLoading,
          })?.loadingDescription
        }
      />
      <RegistrationLayout>
        <h2>Language Proficiency</h2>
        {showEnglishgComfortablility && (
          <div className="my-3">
            <div className="d-flex">
              <h6>Are you comfortable speaking English?</h6>
              <span className="small-text ml-2">
                {showEnglishgComfortablilityOptionalLabel ? (
                  showEnglishgComfortablilityOptionalLabel
                ) : (
                  <div className="responsive-error-asterix-style">
                    {inputFieldMandatoryLabel}
                  </div>
                )}
              </span>
            </div>

            <RadioButtonSelect
              value={booleanRadioValues}
              defaultValue={fluencyInEnglish}
              onValueChange={onLanguageComfortableClick}
            />
            {englishComfortabilityError && (
              <div className="error-text">{englishComfortabilityError}</div>
            )}
          </div>
        )}
        {showLanguageComfortablility && (
          <div className="my-3">
            <div className="d-flex">
              <h6 className="mb-3">
                Select all other languages that you are comfortable with
              </h6>
              <span className="small-text ml-2">
                {showLanguageComfortablilityOptionalLabel ? (
                  showLanguageComfortablilityOptionalLabel
                ) : (
                  <div className="responsive-error-asterix-style">
                    {inputFieldMandatoryLabel}
                  </div>
                )}
              </span>
            </div>

            <OptionField
              options={languageDropdownValue}
              value={secondaryLanguage}
              onChange={onSecondaryLanguageSelect}
              required
              placeholder="Search Language"
              showIndicator
              isMulti
            />
            {otherLangComfortabilityError && (
              <div className="error-text">{otherLangComfortabilityError}</div>
            )}
          </div>
        )}
        {showErrorComponent && <ErrorComponent errorObj={showErrorComponent} />}
      </RegistrationLayout>
      <RegistrationFooterButton
        disableNextButton={isSubmitAllowed}
        onPositiveActionClick={onPositiveActionClick}
        onBackButtonClick={onBackButtonClick}
      />
    </span>
  );
};
